import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { darken } from '@mui/material/styles';

import { ProjectLayoutModel } from '@paragon/contract-library';
import { getLayoutModelsWithPendingChanges, PathUtilities } from '@paragon/layout-models';
import { createScrollbarStyling } from '@paragon/themes';
import { humanCase } from '@paragon/utilities';

import { getSelectedModel, getSelectedPath } from '../store/selectors/grid';
import { projectNameFocusConsumed } from '../store/slices/gridSlice';
import { AssigneeSelect } from './AssigneeSelect';
import { DueDatePicker } from './DueDatePicker';
import { useAppDispatch, useAppSelector } from './hooks';
import { ProjectDrawerActions } from './ProjectDrawerActions';
import { ProjectEstimatesTab } from './ProjectEstimatesTab';
import { ProjectStatusSelect } from './ProjectStatusSelect';
import { ProjectTab } from './ProjectTab';
import ItemProperties from './Tools/PropertiesPanel/ItemProperties';

export const DRAWER_WIDTH = 480;

enum DrawerTab {
  Project,
  Properties,
  Estimates,
}

function getTabForSelectedPath(selectedPath: string | null): DrawerTab {
  return selectedPath != null && !PathUtilities.isProject(selectedPath) ? DrawerTab.Properties : DrawerTab.Project;
}

interface Props {
  readonly projectGuid: string;
  readonly focusProjectName?: boolean;
  readonly onClose: () => void;
}

export function ProjectDetailDrawer({ projectGuid, focusProjectName, onClose }: Props) {
  const dispatch = useAppDispatch();
  const project = useAppSelector(
    (state) => getLayoutModelsWithPendingChanges(state)[projectGuid] as ProjectLayoutModel | undefined,
  );
  const selectedPath = useAppSelector(getSelectedPath);
  const selectedModel = useAppSelector(getSelectedModel);
  const propertiesTabLabel =
    getTabForSelectedPath(selectedPath) === DrawerTab.Properties && selectedModel != null
      ? humanCase(selectedModel.type)
      : null;

  const [activeTab, setActiveTab] = useState<DrawerTab>(getTabForSelectedPath(selectedPath));

  // While the estimate wizard is open it takes over the whole drawer: chrome hidden, fullscreen forced.
  const [wizardOpen, setWizardOpen] = useState(false);

  // Reveal the Properties tab when a non-project item is selected (except while the wizard is open).
  const [followedSelectedPath, setFollowedSelectedPath] = useState(selectedPath);
  if (selectedPath !== followedSelectedPath) {
    setFollowedSelectedPath(selectedPath);
    const selectedPathTab = getTabForSelectedPath(selectedPath);
    if (!wizardOpen && selectedPathTab === DrawerTab.Properties) setActiveTab(selectedPathTab);
  }

  if (wizardOpen && project == null) setWizardOpen(false);

  const startEstimateWizard = () => {
    setActiveTab(DrawerTab.Estimates);
    setWizardOpen(true);
  };
  const visibleTab = activeTab === DrawerTab.Properties && propertiesTabLabel == null ? DrawerTab.Project : activeTab;

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open
      slotProps={{
        // The paper is positioned within the page container (which is `relative`) so the drawer sits
        // below the full-width app header rather than overlapping it.
        paper: {
          sx: {
            width: wizardOpen ? '100%' : DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundImage: 'none',
            position: 'absolute',
            height: '100%',
          },
        },
      }}
    >
      {!wizardOpen && (
        <Box sx={{ display: 'flex', flexDirection: 'column', borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2 }}>
            <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
              {project?.name ?? 'Project'}
            </Typography>
            <Tooltip title="Close" disableInteractive>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ px: 1, pb: 1 }}>
            <ProjectDrawerActions projectGuid={projectGuid} project={project} onCreateEstimate={startEstimateWizard} />
          </Box>

          {project && (
            <Box sx={{ display: 'flex', gap: 1, padding: 1, '&>*': { flex: 1 } }}>
              <ProjectStatusSelect project={project} />
              <AssigneeSelect project={project} />
              <DueDatePicker project={project} />
            </Box>
          )}

          <Tabs
            value={visibleTab}
            onChange={(_, value: DrawerTab) => setActiveTab(value)}
            variant="fullWidth"
            sx={{ bgcolor: (theme) => darken(theme.palette.background.paper, 0.08) }}
          >
            <Tab label="Project" value={DrawerTab.Project} />
            {propertiesTabLabel != null && <Tab label={propertiesTabLabel} value={DrawerTab.Properties} />}
            <Tab label="Estimates" value={DrawerTab.Estimates} />
          </Tabs>
        </Box>
      )}

      <Box sx={[{ flexGrow: 1, overflow: 'auto', minHeight: 0 }, createScrollbarStyling]}>
        {visibleTab === DrawerTab.Project && (
          <ProjectTab
            projectGuid={projectGuid}
            focusProjectName={focusProjectName}
            onProjectNameFocused={() => dispatch(projectNameFocusConsumed())}
          />
        )}
        {visibleTab === DrawerTab.Properties && <ItemProperties />}
        {visibleTab === DrawerTab.Estimates && (
          <ProjectEstimatesTab
            projectGuid={projectGuid}
            project={project}
            wizardOpen={wizardOpen}
            setWizardOpen={setWizardOpen}
          />
        )}
      </Box>
    </Drawer>
  );
}
